<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVisionAccess } from 'dashboard/composables/useVisionAccess';
import { useMapGetter } from 'dashboard/composables/store';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { visionPromptsFor, buildVisionPrompt } from './quickPrompts';

const props = defineProps({
  contactCpf: {
    type: String,
    default: '',
  },
  selectedContract: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['useSuggestion']);
const { t } = useI18n();

// Mesmo gate do menu de atalhos do reply box: agente só vê o atalho cuja
// tool o papel personalizado dele libera (ver quickPrompts.js).
const { visionAllowed, allowedTools } = useVisionAccess();

// Numa conversa cujo contato tem CPF/CNPJ, oferece as perguntas prontas.
// Nome do ERP da conta (SGP, IXC, HubSoft…) vem do jbuilder da conta. Sem
// integração cadastrada, a frase fala em "sistema do provedor" — nunca nomeia
// um ERP que a conta não tem (o "IXC" ficou hardcoded por ter sido o primeiro).
const currentAccountId = useMapGetter('getCurrentAccountId');
const getAccount = useMapGetter('accounts/getAccount');
const erpName = computed(
  () =>
    getAccount.value(currentAccountId.value)?.ai_integration_provider_name ||
    t('VISION.ERP_FALLBACK')
);

const promptOptions = computed(() =>
  props.contactCpf ? visionPromptsFor(allowedTools.value) : []
);

// Papel restrito que não libera nenhuma das tools dos atalhos: em vez de
// sumir com a seção em silêncio, diz por que ela sumiu — a pergunta em
// texto livre continua valendo.
const noPromptsForRole = computed(
  () =>
    !!props.contactCpf &&
    visionAllowed.value &&
    allowedTools.value !== null &&
    promptOptions.value.length === 0
);

const handleSuggestion = opt => {
  emit(
    'useSuggestion',
    buildVisionPrompt(t, opt, props.contactCpf, props.selectedContract)
  );
};
</script>

<template>
  <div class="flex-1 flex flex-col gap-6 px-2">
    <div class="flex flex-col space-y-4 py-4">
      <Icon icon="i-lucide-scan-search" class="text-n-slate-9 text-4xl" />
      <div class="space-y-1">
        <h3 class="text-base font-medium text-n-slate-12 leading-8">
          {{ $t('VISION.PANEL_TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11 leading-6">
          {{ $t('VISION.KICK_OFF_MESSAGE', { erp: erpName }) }}
        </p>
        <p
          v-if="selectedContract"
          class="text-xs text-n-slate-11 leading-5 rounded-md bg-n-alpha-2 px-2 py-1 inline-flex items-center gap-1"
        >
          <Icon icon="i-lucide-file-badge" class="size-3.5" />
          {{
            $t('VISION.SELECTED_CONTRACT', {
              id: selectedContract.id_contrato,
              label: selectedContract.label || '',
            })
          }}
        </p>
      </div>
    </div>
    <p v-if="noPromptsForRole" class="text-xs text-n-slate-10 leading-5">
      {{ $t('VISION.PROMPTS.NONE_FOR_ROLE') }}
    </p>
    <div v-if="promptOptions.length" class="w-full space-y-2">
      <span class="text-xs text-n-slate-10 block">
        {{ $t('VISION.TRY_THESE_PROMPTS') }}
      </span>
      <div class="space-y-1">
        <button
          v-for="prompt in promptOptions"
          :key="prompt.key"
          class="w-full px-3 py-2 rounded-md border border-n-weak bg-n-slate-2 text-n-slate-11 flex items-center justify-between hover:bg-n-slate-3 transition-colors"
          @click="handleSuggestion(prompt)"
        >
          <span>{{ t(prompt.label) }}</span>
          <Icon icon="i-lucide-chevron-right" />
        </button>
      </div>
    </div>
  </div>
</template>
